import React, { useState } from 'react';
import InvalidPermissionsDialog from '../../../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import {
    CreateJobDialog,
    FileData,
    replaceModalConfig,
} from '../create-job-dialog';
import { Grid, Typography } from '@mui/material';
import { styles } from '../upload-data-dictionary/upload-data-dictionary-helper';
import { Checkbox, DragAndDrop } from '@biq/apex-ui';
import { useAuth, usePermission } from '../../../../core';
import { useNavigate } from 'react-router-dom';
import { useDbSchemaUpload } from '../../../../hooks';
import { JOB_MANAGER_PATHS } from 'constants/job-manager-paths';
import { ConfirmationModal } from 'components';
import { styles as confirmationModalStyles } from 'components/confirmation-modal/confirmation-modal-helper';

export const UploadDbSchema = (): React.JSX.Element => {
    const [files, setFiles] = useState<FileData[]>([]);
    const [fileError, setFileError] = useState<boolean>(false);
    const [deleteDictionaries, setDeleteDictionaries] =
        useState<boolean>(false);
    const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const { getToken, getUserEmail } = useAuth();
    const navigate = useNavigate();
    const { hasPermissions } = usePermission();
    const { mutate: uploadDbSchemaUpload } = useDbSchemaUpload();

    const handleConfirmationModal = (): void => {
        setConfirmationModal(false);
    };

    const uploadDbSchemaUploadRequest = (): void => {
        setIsLoading(true);
        setTimeout(() => {
            navigate(JOB_MANAGER_PATHS.LANDING_PAGE);
        }, 500);
        uploadDbSchemaUpload(
            {
                file: files[0],
                token: getToken(),
                email: getUserEmail(),
                deleteDictionaries: deleteDictionaries,
            },
            {
                onSuccess: () => {
                    setIsLoading(false);
                },
                onError: () => {
                    setIsLoading(false);
                },
            }
        );
    };

    const handleSubmit = (): void => {
        setFileError(false);
        if (files.length === 0) {
            setFileError(true);
        } else if (files[0].error === true && files[0].rejected === true) {
            setFileError(true);
        } else {
            setFileError(false);
            if (deleteDictionaries) {
                setConfirmationModal(true);
            } else {
                uploadDbSchemaUploadRequest();
            }
        }
    };

    const onSubmit = (): void => {
        const permission = hasPermissions(['createData', 'createMetadata']);
        if (permission) {
            handleSubmit();
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    return (
        <CreateJobDialog
            buttonText={'Start Upload'}
            handleSubmit={onSubmit}
            isLoading={isLoading}
        >
            <Grid mt={1.5}>
                <Typography sx={styles.uploadDataDictionaryText}>
                    Data model file must be a DBS file and will likely contain
                    more than one
                    <br />
                    dictionary and more than one edge.
                </Typography>
            </Grid>
            <Grid mt={3} mx={'143.5px'}>
                <Checkbox
                    label={'Delete dictionaries not specified by the DbSchema'}
                    checked={deleteDictionaries}
                    onChange={(__, checked: boolean): void =>
                        setDeleteDictionaries(checked)
                    }
                />
            </Grid>
            <Grid mt={3} mx={'143.5px'}>
                <Grid>
                    <DragAndDrop
                        files={files}
                        setFiles={setFiles}
                        fileError={fileError}
                        setFileError={setFileError}
                        filePreview={true}
                        showReplaceFileModal={true}
                        replaceModalConfig={replaceModalConfig}
                    />
                </Grid>
            </Grid>
            {confirmationModal && (
                <ConfirmationModal
                    confirmationModal={confirmationModal}
                    handleConfirmationModal={handleConfirmationModal}
                    confirmationKey={'DELETE'}
                    confirmationButtonText={'Delete Dictionaries'}
                    confirmationHeadingText={'Confirm Dictionary Deletion'}
                    confirmationInputLabelText={'Type DELETE below'}
                    handleConfirmation={uploadDbSchemaUploadRequest}
                    confirmationSubHeadingText={
                        <Typography>
                            By typing{' '}
                            <Typography
                                component='span'
                                sx={confirmationModalStyles.textBoldStyles}
                            >
                                ‘DELETE’
                            </Typography>{' '}
                            below, you understand that you are permanently
                            deleting this dictionaries not specified by this DB
                            Schema. This action{' '}
                            <Typography
                                component='span'
                                sx={confirmationModalStyles.textBoldStyles}
                            >
                                CANNOT
                            </Typography>{' '}
                            be undone.
                        </Typography>
                    }
                />
            )}
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </CreateJobDialog>
    );
};
